import { Hidden } from '@mui/material'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ConsolidationPagination } from 'src/components/ConsolidationPagination/ConsolidationPagination'
import { Footer } from 'src/components/Footer/Footer'
import { Breadcrumb } from 'src/components/global/Breadcrumb'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { TabButton } from 'src/components/Tabs/TabButton'
import { SHOW_INTEGRATION_FILTER_TO_ALL, THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { useAuthDefender } from 'src/hooks/useAuthDefender'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDecodedKeyword } from 'src/hooks/useGetDecodeKeyword'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption, FilterOptionItem, TaxonomyType } from 'src/types'
import { getProductTypesFilter } from 'src/utils/ContentFilters/contentFilters'

type Props = {
  filters: FilterOption[]
}

const GraveyardConsolidationPage: NextPage<Props> = (props: Props) => {
  const router = useRouter()

  const { updateQueryParams } = useUpdateQueryParams()
  const { session, status, isPremium } = useAuthenticated()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    // Only true if it's running on the client side
    setIsClient(true)
  }, [])

  useAuthDefender()

  useEffect(() => {
    if (
      isClient &&
      !isPremium &&
      session?.user?.id &&
      session?.user?.id != '' &&
      session?.user?.id != null &&
      status == 'authenticated'
    ) {
      router.push('/')
    }
  }, [isClient, isPremium, session])

  if (!isClient) {
    return null // Or a loading spinner while waiting for the client-side
  }
  const tab: string = (router.query.tab as string) || 'graveyard'
  const decodedSearchKeyword = useDecodedKeyword(router.query.keyword)

  const meta = (
    <Meta
      title={tab == 'graveyard' ? 'Graveyard' : 'Consolidations'}
      description={tab == 'graveyard' ? 'Graveyard' : 'Consolidations'}
      url="/graveyard-consolidations"
    />
  )
  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <div className="flex flex-col bg-clouds pl-[12px] pr-[12px]">
          <Breadcrumb
            subject={'INDUSTRY ANALYSIS'}
            subjectRoute={'/industry-analysis'}
            subtitle={tab == 'graveyard' ? 'GRAVEYARD' : 'CONSOLIDATIONS'}
            subtopicRoute={`/graveyard-consolidations/?tab=${tab}`}
          />
          <div className="flex flex-col px-5 sm:px-0 justify-center items-center">
            <div className="!max-w-[1100px] w-full flex flex-row items-center py-2 mt-[27PX] mb-[31px]">
              <h1 className="h-[50px] text-darkgray font-secondary font-bold text-[30px]">
                {tab == 'graveyard' ? 'Graveyard' : 'Consolidations'}
              </h1>
            </div>
            <div className="flex md:!max-w-[1100px] w-full h-[3.125rem] md:mr-[80px] md:mt-[0rem] mt-[3.75rem] justify-center">
              <TabButton
                className="w-[147px]"
                isSelected={tab === 'graveyard'}
                onClick={() =>
                  updateQueryParams({
                    tab: 'graveyard',
                    page: '',
                    sort: decodedSearchKeyword ? 'bykeyword' : 'Default',
                  })
                }
              >
                GRAVEYARD
              </TabButton>

              <TabButton
                className="w-[185px]"
                isSelected={tab === 'consolidations'}
                onClick={() =>
                  updateQueryParams({
                    tab: 'consolidations',
                    page: '',
                    sort: decodedSearchKeyword ? 'bykeyword' : 'Default',
                  })
                }
              >
                CONSOLIDATIONS
              </TabButton>
            </div>
          </div>
        </div>
        <hr />
        <div className="flex flex-row justify-center w-full">
          <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
            <Hidden mdDown>
              <div className="w-1/4 pr-10 flex flex-col">
                <SearchField className="my-8" />
                <Sidebar filterOptions={props.filters} defaultSelectFilterIdx={1} />
              </div>
            </Hidden>
            <ConsolidationPagination filterProps={props.filters} type={tab || ''} />
          </div>
        </div>
        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default GraveyardConsolidationPage

export const getStaticProps: GetStaticProps<Props> = async ({}) => {
  if (THEME === 'ilta')
    return {
      revalidate: 60,
      notFound: true,
    }
  try {
    const [
      { integrations },
      { demographics },
      productTypes,
      { attributes },
      { installations },
      { platformLanguages },
      { practiceAreas },
      { regionsServeds },
      { hqs },
    ] = await Promise.all([
      // if ilta theme, don't show integration filter (line number 124 already apply the theme filter)
      // if show integration filter to all, show all integrations, otherwise show empty array
      SHOW_INTEGRATION_FILTER_TO_ALL ? apolloSdk.getIntegrations() : { integrations: [] },
      apolloSdk.getAudiences(),
      getProductTypesFilter(),
      apolloSdk.getAttributes(),
      apolloSdk.getInstallations(),
      apolloSdk.getLanguages(),
      apolloSdk.getPracticeAreas(),
      apolloSdk.getRegionsServed(),
      apolloSdk.hqs(),
    ])

    // this code is using for unique integrations or remove duplicates by name
    /* const unique: any[] = []
    const seen = new Set()

    for (const item of integrations?.data || []) {
      const name = item?.attributes?.name
      if (!seen.has(name) && name) {
        seen.add(name)
        unique.push(item)
      }
    } */

    const demographicsData = demographics
      .map((item) => ({
        id: item?.documentId,
        name: item?.name,
      }))
      .filter((item): item is FilterOptionItem => !!item.id && !!item.name)

    const attributesData = attributes
      .map((item) => ({
        id: item?.documentId,
        name: item?.name,
      }))
      .filter((item): item is FilterOptionItem => !!item.id && !!item.name)

    const installationssData =
      installations
        .map((item) => ({
          id: item?.documentId,
          name: item?.name,
        }))
        .filter((item): item is FilterOptionItem => !!item?.id && !!item?.name) || []

    const platformLanguagesData = platformLanguages
      .map((item) => ({
        id: item?.documentId,
        name: item?.name,
      }))
      .filter((item): item is FilterOptionItem => !!item.id && !!item.name)

    const integrationsData =
      integrations
        .map((item) => ({
          id: item?.documentId,
          name: item?.name,
        }))
        .filter((item): item is FilterOptionItem => !!item.id && !!item.name) || []

    const practiceAreasData = practiceAreas
      .map((item) => ({
        id: item?.documentId,
        name: item?.name,
      }))
      .filter((item): item is FilterOptionItem => !!item.id && !!item.name)
    const regionsServedData = regionsServeds
      .map((item) => ({
        id: item?.documentId,
        name: item?.name,
      }))
      .filter((item): item is FilterOptionItem => !!item.id && !!item.name)

    const hqsData = hqs
      .map((item) => ({
        id: item?.documentId,
        name: item?.name,
      }))
      .filter((item): item is FilterOptionItem => !!item.id && !!item.name)

    const filters = []

    filters.push({ title: TaxonomyType.Audience, data: demographicsData })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters.push({ title: TaxonomyType.ProductType, data: (productTypes?.data || []) as any })
    filters.push({ title: TaxonomyType.PracticeArea, data: practiceAreasData })
    filters.push({ title: TaxonomyType.Language, data: platformLanguagesData })
    filters.push({ title: TaxonomyType.Deployment, data: installationssData })
    filters.push({ title: TaxonomyType.Integrations, data: integrationsData })
    filters.push({ title: TaxonomyType.Attribute, data: attributesData })
    filters.push({ title: TaxonomyType.RegionsServed, data: regionsServedData })
    filters.push({ title: TaxonomyType.HQ, data: hqsData })

    return {
      props: {
        filters,
      },
      revalidate: 60,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of Graveyard & Consolidations:', error)
    // Graceful fallback in case of an upstream error
    return {
      notFound: true,
      revalidate: 5, // Reattempt fetching during the next static regeneration
    }
  }
}
